import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { toast } from "react-toastify";

import { API_TOKEN, API_URL } from "../../Config/secrets";

const initialState = {
  matchLoading: false,
  filterLoading: false,
  matchItems: [],
  matchTotal: 0,
  matchTotalPages: 0,
  filterData: {},
  error: null,
};

const slice = createSlice({
  name: "matches",
  initialState,
  reducers: {
    startMatchLoading(state) {
      state.matchLoading = true;
    },
    startFilterLoading(state) {
      state.filterLoading = true;
    },
    stopFilterLoading(state) {
      state.filterLoading = false;
    },
    hasError(state, action) {
      state.matchLoading = false;
      state.filterLoading = false;
      state.error = action.payload;
    },
    getMatchesSuccess(state, action) {
      const { items, total, totalPages, append } = action.payload;
      state.matchLoading = false;
      state.matchTotal = total;
      state.matchTotalPages = totalPages;
      if (append) {
        state.matchItems = [...state.matchItems, ...items];
      } else {
        state.matchItems = items;
      }
    },
    getDataToFilterSuccess(state, action) {
      state.filterLoading = false;
      state.filterData = action.payload;
    },
    updateMatchSuccess(state, action) {
      const updated = action.payload;
      const idx = state.matchItems.findIndex((m) => m.id === updated.id);
      if (idx !== -1) {
        state.matchItems[idx] = { ...state.matchItems[idx], ...updated };
      }
    },
    resetMatches: () => initialState,
  },
});

export default slice.reducer;

const header = { Authorization: API_TOKEN };

export function getMatches({
  id,
  tab,
  entity_type,
  skip,
  take,
  matched_by_ai,
  query,
  filters,
  append = false,
}) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startMatchLoading());

      const payload = { id, tab, entity_type, skip, take };

      if (matched_by_ai !== undefined) payload.matched_by_ai = matched_by_ai;
      if (query) payload.query = query;

      if (filters) {
        const cleanFilters = Object.fromEntries(
          Object.entries(filters).filter(
            ([, v]) => Array.isArray(v) && v.length > 0
          )
        );
        if (Object.keys(cleanFilters).length > 0) payload.filters = cleanFilters;
      }

      const response = await axios.post(
        `${API_URL}/matches/get-matches`,
        payload,
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        dispatch(
          slice.actions.getMatchesSuccess({
            items: response.data.data?.items || [],
            total: response.data.data?.total || 0,
            totalPages: response.data.data?.totalPages || 0,
            append,
          })
        );
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
      }

      return response?.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function getDataToFilter({ id, tab, entity_type, matched_by_ai }) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startFilterLoading());

      const response = await axios.post(
        `${API_URL}/matches/get-data-to-filter`,
        { id, tab, entity_type, matched_by_ai },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        dispatch(slice.actions.getDataToFilterSuccess(response.data.data || {}));
      } else {
        dispatch(slice.actions.stopFilterLoading());
      }

      return response?.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function convertRetailerToBrand(retailer_id) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/brands/convert-retailer-to-brand`,
        { retailer_id },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response.data.message || "Converted to brand successfully");
      } else {
        toast.error(response?.data?.message || "Conversion failed");
      }

      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Conversion failed");
      return error?.response?.data;
    }
  };
}

export function convertBrandToRetailer(brand_id) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/retailers/convert-brand-to-retailer`,
        { brand_id },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response.data.message || "Converted to retailer successfully");
      } else {
        toast.error(response?.data?.message || "Conversion failed");
      }

      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Conversion failed");
      return error?.response?.data;
    }
  };
}

export function updateMatch(payload) {
  return async (dispatch) => {
    try {
      const response = await axios.put(
        `${API_URL}/matches/update-match`,
        payload,
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response.data.message || "Grade updated");
        if (response.data.data) {
          dispatch(slice.actions.updateMatchSuccess(response.data.data));
        }
      } else {
        toast.error(response?.data?.message || "Failed to update grade");
      }

      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update grade");
      return error?.response?.data;
    }
  };
}

export function bulkDeleteMatches({
  id,
  tab,
  entity_type,
  matched_by_ai,
  query,
  filters,
}) {
  return async () => {
    try {
      const payload = { id, tab, entity_type, skip: 0, take: 1000000 };

      if (matched_by_ai !== undefined) payload.matched_by_ai = matched_by_ai;
      if (query) payload.query = query;

      if (filters) {
        const cleanFilters = Object.fromEntries(
          Object.entries(filters).filter(
            ([, v]) => Array.isArray(v) && v.length > 0
          )
        );
        if (Object.keys(cleanFilters).length > 0) payload.filters = cleanFilters;
      }

      const response = await axios.post(
        `${API_URL}/matches/bulk-delete-matches`,
        payload,
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response.data.message || "Matches deleted successfully");
      } else {
        toast.error(response?.data?.message || "Failed to delete matches");
      }

      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete matches");
      return error?.response?.data;
    }
  };
}

export function fetchMatchesLocations({ id, entity_type, tab }) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/matches/fetch-matches-locations`,
        { id, entity_type, tab },
        { headers: header },
      );

      if (response?.data?.statusCode === 200) {
        const { totalFetches, pending } = response.data.data || {};

        if (totalFetches === 0) {
          toast.info(response.data.message || "No matches without store locations found");
        } else if (pending) {
          toast.info(response.data.message || "Fetching store locations. This may take a while.");
        } else {
          toast.success(response.data.message || "Store locations fetched successfully");
        }

        return response.data;
      }

      toast.error(response?.data?.message || "Failed to fetch match locations");
      return response?.data;
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch match locations",
      );
      return error?.response?.data;
    }
  };
}
